/**
 * Which face an app area shows. An app that needs the person's own
 * connection (iManage today; any app whose definition carries a `landing`)
 * opens on its landing page until it is connected, and straight into the app
 * after. While the connection is unknown the area shows a skeleton, so a
 * connected person never sees the landing flash by.
 *
 * The landing renders at the address that was asked for, so a deep link
 * opened while disconnected becomes that page the moment the connection
 * lands — there is no redirect to undo.
 *
 * Pure: the layout (sidebar or not), the area and the tests read the same
 * answer.
 *
 * @module components/apps/appGate
 */

/** What is known about the person's connection to an app. */
export type AppConnection =
  /** Not read yet (or the organization is not known yet). */
  | "unknown"
  | "connected"
  /** Read, and not connected: not set up, not signed in, or signing in. */
  | "not-connected"
  /** The status could not be read at all. */
  | "unavailable";

/** loading: a skeleton · landing: the app's landing page, no area sidebar · app: the app itself. */
export type AppAreaView = "loading" | "landing" | "app";

export function appAreaView(input: {
  /** The app needs a connection (its definition has a landing, and a gate reads it). */
  readonly gated: boolean;
  readonly connection: AppConnection;
  /**
   * A page that must stay reachable while disconnected — Settings, where an
   * admin sets the app up. It still waits for the status, and an unreadable
   * status still lands on the landing, which explains it.
   */
  readonly exempt: boolean;
}): AppAreaView {
  if (!input.gated) return "app";
  switch (input.connection) {
    case "unknown":
      return "loading";
    case "connected":
      return "app";
    case "unavailable":
      return "landing";
    case "not-connected":
      return input.exempt ? "app" : "landing";
  }
}

/**
 * Whether the Apps sidebar sits beside the page: only inside an app area, and
 * not on a landing, which is one full page like the launcher. While loading
 * the sidebar stays, so a connected person's area does not jump when the
 * status arrives.
 */
export function appsSidebarShown(inArea: boolean, view: AppAreaView | null): boolean {
  return inArea && view !== "landing";
}

/**
 * A remembered view is reopened only when it is still inside the same app of
 * the same organization: a same-origin path, never a protocol or `//host`.
 */
export function resumableAppHref(
  stored: string | null,
  orgSegment: string,
  appId: string,
): string | null {
  if (!stored || stored.length > 2000) return null;
  const base = `/${orgSegment}/apps/${appId}`;
  if (!stored.startsWith(base)) return null;
  const next = stored.charAt(base.length);
  if (next !== "" && next !== "/" && next !== "?") return null;
  if (stored.includes("//") || stored.includes("\\")) return null;
  return stored;
}

/** The storage key for an app's last view, per organization (its URL segment). */
export const appLastViewKey = (orgSegment: string, appId: string): string =>
  `amoreg:apps:last-view:v1:${orgSegment}:${appId}`;

/**
 * A connection from a status read: unknown until the organization is known
 * and the first read has answered; unavailable when it failed with nothing
 * to show.
 */
export function connectionFromStatus(
  query: {
    readonly data: { readonly connected: boolean } | null;
    readonly loading: boolean;
  },
  organizationKnown: boolean,
): AppConnection {
  if (!organizationKnown) return "unknown";
  if (query.data) return query.data.connected ? "connected" : "not-connected";
  return query.loading ? "unknown" : "unavailable";
}
